// src/hooks/usePermissionGuard.ts
import { useState, useCallback } from 'react';
import { PopupType } from '../components/Popup';
import { getPermissionError, PermissionError } from '../utils/permissionErrors';

interface GuardOptions {
  showPopup?: boolean;
  onError?: (error: PermissionError) => void;
}

export function usePermissionGuard() {
  const [popup, setPopup] = useState<{
    isOpen: boolean;
    type: PopupType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'error',
    title: '',
    message: '',
  });

  const showError = useCallback((errorCode: string, customMessage?: string) => {
    const error = getPermissionError(errorCode);
    setPopup({
      isOpen: true,
      type: 'error',
      title: error.title,
      message: customMessage || error.message + (error.suggestion ? `\n\n${error.suggestion}` : ''),
    });
  }, []);

  const showWarning = useCallback((title: string, message: string) => {
    setPopup({
      isOpen: true,
      type: 'warning',
      title,
      message,
    });
  }, []);

  const showSuccess = useCallback((title: string, message: string) => {
    setPopup({
      isOpen: true,
      type: 'success',
      title,
      message,
    });
  }, []);

  const showInfo = useCallback((title: string, message: string) => {
    setPopup({
      isOpen: true,
      type: 'info',
      title,
      message,
    });
  }, []);

  const closePopup = useCallback(() => {
    setPopup(prev => ({ ...prev, isOpen: false }));
  }, []);

  const guard = useCallback(async <T,>(
    action: () => Promise<T>,
    errorCode: string,
    customMessage?: string
  ): Promise<T | null> => {
    try {
      return await action();
    } catch (error: any) {
      const backendMessage = error?.response?.data?.detail || error?.message;
      
      // Check for specific backend error messages
      if (backendMessage?.includes('cannot assign') || backendMessage?.includes('Cannot assign')) {
        showError('CANNOT_ASSIGN_TO_HIGHER_LEVEL', backendMessage);
      } else if (backendMessage?.includes('open subtask')) {
        showError('CANNOT_COMPLETE_WITH_OPEN_SUBTASKS', backendMessage);
      } else if (backendMessage?.includes('permission') || backendMessage?.includes('Permission')) {
        showError('CANNOT_MODIFY_OTHERS_TASK', backendMessage);
      } else if (backendMessage?.includes('not found')) {
        showError('TASK_NOT_FOUND', backendMessage);
      } else if (backendMessage?.includes('Network Error')) {
        showError('NETWORK_ERROR', backendMessage);
      } else {
        showError(errorCode, customMessage || backendMessage);
      }
      return null;
    }
  }, [showError]);

  return {
    popup,
    showError,
    showWarning,
    showSuccess,
    showInfo,
    closePopup,
    guard,
  };
}